import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSubdomainOrganization } from '@/hooks/useSubdomainOrganization';
import { useAuth } from '@/context/auth-provider';
import { Building2, ExternalLink, Settings } from 'lucide-react';
import { clearThemeCache, resetCssVariables } from '@/lib/subdomain-utils';

interface Organization {
  id: string;
  subdomain: string;
  name: string;
  display_name?: string;
  logo_url?: string;
  is_active: boolean;
}

export const OrganizationSwitcher = () => {
  const { user } = useAuth();
  const { organization: currentOrg, subdomain } = useSubdomainOrganization();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const queryClient = useQueryClient();

  // Fetch user's organizations
  const { data: userOrganizations, isLoading } = useQuery({
    queryKey: ['user-organizations', user?.id],
    queryFn: async (): Promise<Organization[]> => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('organization_user_roles')
        .select(`
          org_id,
          role,
          organizations!organization_user_roles_org_id_fkey (
            id,
            subdomain,
            name,
            display_name,
            logo_url,
            is_active
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;

      return data
        ?.filter(item => item.organizations && item.organizations.is_active)
        .map(item => item.organizations as Organization) || [];
    },
    enabled: !!user?.id,
  });

  const handleSwitchOrganization = async (orgSubdomain: string) => {
    if (orgSubdomain === subdomain) {
      setIsOpen(false);
      return;
    }

    // CRITICAL: Sign out to prevent auth session bleeding across organizations
    console.log('[ORG-SWITCH] Signing out before switching organizations');
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('[ORG-SWITCH] Error signing out:', error);
    }

    // Clear React Query cache to prevent stale data
    queryClient.clear();
    
    // Clear all theme caches to prevent color bleeding
    // NOTE: Do NOT clear org-id-${subdomain} cache - it's needed for instant theme loading after login
    clearThemeCache();
    
    // Reset CSS variables to defaults
    resetCssVariables();
    
    console.log('[ORG-SWITCH] Cleared caches and reset CSS variables');

    // In development, use localStorage to switch organizations
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      localStorage.setItem('dev-organization', orgSubdomain);
      // Navigate to auth page for re-login
      window.location.href = '/auth';
      return;
    }

    // In production, redirect to the subdomain auth page for re-login
    const currentUrl = new URL(window.location.href);
    const newUrl = `${currentUrl.protocol}//${orgSubdomain}.${currentUrl.hostname.split('.').slice(1).join('.')}/auth`;
    window.location.href = newUrl;
  };

  const handleOpenInNewTab = (orgSubdomain: string) => {
    let newUrl: string;
    
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      // For development, open with org parameter
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('org', orgSubdomain);
      newUrl = currentUrl.toString();
    } else {
      // For production, use subdomain
      const currentUrl = new URL(window.location.href);
      newUrl = `${currentUrl.protocol}//${orgSubdomain}.${currentUrl.hostname.split('.').slice(1).join('.')}${currentUrl.pathname}`;
    }
    
    window.open(newUrl, '_blank');
  };

  if (!user || !userOrganizations?.length || userOrganizations.length <= 1) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <Building2 className="h-4 w-4" />
          <span className="hidden md:inline">
            {currentOrg?.display_name || currentOrg?.name || subdomain}
          </span>
          <Badge variant="outline" className="text-xs">
            {userOrganizations.length}
          </Badge>
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Switch Organization
          </DialogTitle>
          <DialogDescription>
            Choose which organization you'd like to access.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {userOrganizations.map((org) => {
            const isCurrent = org.subdomain === subdomain;
            
            return (
              <Card 
                key={org.id} 
                className={`cursor-pointer transition-colors hover:bg-muted/50 ${
                  isCurrent ? 'ring-2 ring-primary bg-primary/5' : ''
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        {org.logo_url ? (
                          <img 
                            src={org.logo_url} 
                            alt={`${org.name} logo`}
                            className="w-8 h-8 rounded-lg object-contain"
                            style={{ maxWidth: '100%', maxHeight: '100%' }}
                          />
                        ) : (
                          <Building2 className="h-5 w-5 text-primary" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">
                            {org.display_name || org.name}
                          </h4>
                          {isCurrent && (
                            <Badge variant="default" className="text-xs">
                              Current
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {org.subdomain}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      {!isCurrent && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenInNewTab(org.subdomain)}
                            title="Open in new tab"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSwitchOrganization(org.subdomain)}
                          >
                            Switch
                          </Button>
                        </>
                      )}
                      {isCurrent && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsOpen(false)}
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center pt-2 border-t">
          <p className="text-xs text-muted-foreground">
            You have access to {userOrganizations.length} organization{userOrganizations.length !== 1 ? 's' : ''}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};